/*
Copyright (c) 2025 Mathilde Meyer, Naziha Beghdadi
Licensed under the MIT License (see LICENSE.txt file for details)
*/

import React, { useEffect, useState } from "react";
import Acclimatation from "./Acclimatation";
import Preparation from "./Preparation";
import TypeIntervention from "./TypeIntervention";
import Section29Form from "./Section29Form";
import Section30Form from "./Section30Form";
import Section36Form from "./Section36Form";
import { Alert, Box, Stack, Chip, Button, Stepper, Step, StepLabel, TextField, Typography, IconButton } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const experimentSteps = [
  "Step 1: Procedures & Timeline",
  "Step 2: Study Design & Animal Counts",
  "Step 3: Justify Animal Numbers",
];

function ExperimentForm({ index, data, setData, onLocalStepChange }) {
  const [localStep, setLocalStep] = useState(0);

  useEffect(() => {
    setLocalStep(0);
  }, [index]);

  useEffect(() => {
    if (onLocalStepChange) {
      onLocalStepChange(localStep);
    }
  }, [localStep, onLocalStepChange]);

  if (!data[index]) {
    return <div style={{ padding: "2rem" }}>Loading project data...</div>;
  }

  const handleAddIntervention = (position) => {
    const newData = [...data];
    const interventions = [...newData[index].interventions];
    interventions.splice(position + 1, 0, { lastIntervention: "yes" });
    newData[index].interventions = interventions;
    setData(newData);
  };

  const handleDeleteIntervention = (position) => {
    const newData = [...data];
    const interventions = [...newData[index].interventions];
    if (interventions.length > 1) {
      interventions.splice(position, 1);
      newData[index].interventions = interventions;
      setData(newData);
    }
  };

  return (
    <div>
      <Box sx={{ mb: 4 }}>
        <Stepper activeStep={localStep} alternativeLabel>
          {experimentSteps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
          This form is grouped into pages. Use the tracker above to see which part of the Form A content you are filling in now.
        </Typography>
      </Box>

      {localStep === 0 && (
        <>
          <Box sx={{ mb: 2, px: 2, py: 1, borderLeft: "4px solid", borderColor: "primary.main", backgroundColor: "primary.50" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.main" }}>
              Section 23 — Course of the experiments: Schematic representation
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Inputs below generate the timeline visualization and auto-fill Sections 24 and 25 of the export.
            </Typography>
          </Box>
          <Box sx={{ mb: 2, p: 2, borderRadius: 2, backgroundColor: "#f7f7f7", border: "1px solid #ddd", fontSize: "0.88rem", lineHeight: 1.65 }}>
            <strong>Step 1:</strong> Describe your experiment here. Information provided will output a schematic timeline and feed sections 24 to 27 of the Form A.
          </Box>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Experiment name / label"
              placeholder="e.g. Objective 1 – Experiment 1a"
              value={data[index].experimentObjective || ""}
              onChange={(e) => {
                const newData = [...data];
                newData[index] = { ...newData[index], experimentObjective: e.target.value };
                setData(newData);
              }}
              helperText="Give this experiment a short name. It will be reused automatically in Section 29."
            />
          </Box>
          <Acclimatation index={index} data={data} setData={setData} />
          <Preparation index={index} data={data} setData={setData} />

          {data[index].interventions.map((intervention, indexIntervention) => (
            <Box
              key={indexIntervention}
              sx={{
                mt: indexIntervention > 0 ? 3 : 0,
                p: 2,
                border: "1px solid #ccc",
                borderRadius: 2,
              }}
            >
              <TypeIntervention
                index={index}
                data={data}
                setData={setData}
                indexIntervention={indexIntervention}
              />

              <Stack direction="row" spacing={1} mt={2} justifyContent="flex-end">
                <Chip
                  onClick={() => handleAddIntervention(indexIntervention)}
                  icon={
                    <AddCircleOutlineIcon
                      fontSize="small"
                      sx={{ color: indexIntervention === data[index].interventions.length - 1 ? "grey.400" : "primary.main" }}
                    />
                  }
                  variant="contained"
                  sx={{ borderColor: "grey.400", "&:hover": { backgroundColor: "grey.100" } }}
                  disabled={indexIntervention === data[index].interventions.length - 1}
                />
                <Chip
                  onClick={() => handleDeleteIntervention(indexIntervention)}
                  icon={
                    <DeleteOutlineIcon
                      fontSize="small"
                      sx={{ color: data[index].interventions.length === 1 ? "grey.400" : "error.main" }}
                    />
                  }
                  variant="contained"
                  sx={{ borderColor: "grey.400", "&:hover": { backgroundColor: "grey.100" } }}
                  disabled={data[index].interventions.length === 1}
                />
              </Stack>
            </Box>
          ))}

          {/* Section 25 additional notes */}
          <Box sx={{ mt: 3, p: 2.5, border: "1px solid #ddd", borderRadius: 2, backgroundColor: "#fafafa" }}>
            <Box sx={{ mb: 2, px: 2, py: 1, borderLeft: "4px solid", borderColor: "primary.main", backgroundColor: "primary.50" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.main" }}>
                Section 25 — Description of procedures (you may skip this and add information later)
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, fontSize: "0.83rem", lineHeight: 1.65 }}>
              Use this field to paste or type a detailed description of the experimental procedures selected above.
              If your procedures are described in SOPs, just mention the name of the procedure and the corresponding SOP number.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={6}
              size="small"
              label="Description of procedures"
              placeholder={"Example:\n• IP injection: 200 µL of compound X (10 mg/kg in PBS) on each intervention day.\n• Anaesthesia: isoflurane induction 2–3%, maintenance 1.5%, O₂ 1 L/min.\n• Blood sampling: 100 µL retro-orbital, once per animal.\n• Euthanasia: pentobarbital injection followed by cervical dislocation.\nAll procedures performed by trained staff (Module 1)."}
              value={data[index].section25Notes || ""}
              onChange={(e) => {
                const newData = [...data];
                newData[index] = { ...newData[index], section25Notes: e.target.value };
                setData(newData);
              }}
            />
            {/* File attachment */}
            <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Button
                component="label"
                size="small"
                variant="outlined"
                startIcon={<AttachFileIcon />}
              >
                Attach protocol file
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (file.size > 10 * 1024 * 1024) {
                      alert("File is too large (max 10 MB). Please attach a smaller file.");
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const newData = [...data];
                      newData[index] = {
                        ...newData[index],
                        section25File: { name: file.name, type: file.type, content: ev.target.result },
                      };
                      setData(newData);
                    };
                    reader.readAsDataURL(file);
                    e.target.value = "";
                  }}
                />
              </Button>
              {data[index].section25File && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.5, borderRadius: 1, border: "1px solid #c8d8e0", backgroundColor: "#f0f7fb", fontSize: "0.83rem" }}>
                  <AttachFileIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2" sx={{ fontSize: "0.83rem" }}>{data[index].section25File.name}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newData = [...data];
                      newData[index] = { ...newData[index], section25File: null };
                      setData(newData);
                    }}
                    sx={{ ml: 0.5, p: 0.2 }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">PDF, Word or TXT — max 10 MB</Typography>
            </Box>
          </Box>
        </>
      )}

      {localStep === 1 && (
        <>
          <Box sx={{ mb: 2, p: 2, borderRadius: 2, backgroundColor: "#f7f7f7", border: "1px solid #ddd" }}>
            <strong>Step 2:</strong> Describe the design of the experiment, then enter animal groups, lines and numbers. Assign experiments to objectives formulated in section 20 or 22 of the form A.
          </Box>
          {(() => {
            const exp = data[index];
            const hasTimeline =
              parseInt(exp.experimentDuration || "0", 10) > 0 ||
              (exp.interventions || []).some(
                (iv) =>
                  (iv.daysStart && String(iv.daysStart).trim() !== "") ||
                  (iv.specificDays && String(iv.specificDays).trim() !== "")
              );
            if (!hasTimeline) {
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>No timeline data detected for this experiment.</strong> The Form A requires a timeline
                  (Section 23) for every experiment. Go back to <strong>Step 1</strong> and fill in the experiment
                  duration and intervention days, or the generated document will be incomplete.
                </Alert>
              );
            }
            return null;
          })()}
          <Section29Form index={index} data={data} setData={setData} />
          <Section36Form index={index} data={data} setData={setData} />
        </>
      )}

      {localStep === 2 && (
        <>
          <Box sx={{ mb: 2, p: 2, borderRadius: 2, backgroundColor: "#f7f7f7", border: "1px solid #ddd" }}>
            <strong>Step 3:</strong> Use the guided questionnaire to justify the animal numbers for Section 30.
          </Box>
          <Section30Form index={index} data={data} setData={setData} />
        </>
      )}

      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => setLocalStep((prev) => Math.max(0, prev - 1))}
          disabled={localStep === 0}
        >
          Previous Step
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={() => { setLocalStep((prev) => Math.min(experimentSteps.length - 1, prev + 1)); window.scrollTo(0, 0); }}
          disabled={localStep === experimentSteps.length - 1}
        >
          Next Step
        </Button>
      </Box>
    </div>
  );
}

export default ExperimentForm;
